import { createApi } from '@reduxjs/toolkit/query/react'
import { FxRateHistory } from '@/features/portal-configuration/portal-configuration.def.ts'
import {
  GetFxRateHistoriesRequest,
  GetFxRateHistoryRequest,
  ImportFxRateHistoriesRequest,
  UpdateFxRateHistoryRequest,
} from '@/features/portal-configuration/portal-configuration-request.def.ts'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import {
  CreateWorkableEntityConfigRequest,
  DeleteWorkableEntityConfigRequest,
  GetWorkableEntityConfigRequest,
  UpdateWorkableEntityConfigRequest,
  WorkableEntityConfigResponseItem,
} from '@/features/portal-configuration/workable-configuration-request.def.ts'
import {
  GetAllEmployeeDataAccessClaimsResponse,
} from '@/features/portal-configuration/employee-data-access-claim.def.ts'

export const portalConfigurationApi = createApi({
  reducerPath: 'portalConfigurationApi',
  tagTypes: ['fxRateHistory', 'workableEntityConfig', 'employeeDataAccessClaim'],
  baseQuery: hcPortalBaseQuery(),
  endpoints: builder => ({
    getWorkableEntityConfig: builder.query<WorkableEntityConfigResponseItem | null, GetWorkableEntityConfigRequest>({
      query: (request) => {
        return {
          url: '/WorkableConfiguration/GetWorkableEntityConfig',
          method: 'GET',
          cache: 'no-cache',
          params: request,
        }
      },
      providesTags: (_result, _error, arg) => {
        return [{ type: 'workableEntityConfig', id: arg.id }]
      },
    }),
    getWorkableEntityConfigs: builder.query<WorkableEntityConfigResponseItem[], void>({
      query: () => {
        return {
          url: '/WorkableConfiguration/GetWorkableEntityConfigs',
          method: 'GET',
          cache: 'no-cache',
        }
      },
      providesTags: [{ type: 'workableEntityConfig', id: 'LIST' }],
    }),
    createWorkableEntityConfig: builder.mutation<WorkableEntityConfigResponseItem, CreateWorkableEntityConfigRequest>({
      query: (request) => {
        return {
          url: '/WorkableConfiguration/CreateWorkableEntityConfig',
          method: 'POST',
          cache: 'no-cache',
          body: request,
        }
      },
      invalidatesTags: () => {
        return [
          { type: 'workableEntityConfig', id: 'LIST' },
        ]
      },
    }),
    updateWorkableEntityConfig: builder.mutation<WorkableEntityConfigResponseItem, UpdateWorkableEntityConfigRequest>({
      query: (request) => {
        return {
          url: '/WorkableConfiguration/UpdateWorkableEntityConfig',
          method: 'POST',
          cache: 'no-cache',
          body: request,
        }
      },
      invalidatesTags: (_result, _error, arg) => {
        return [
          { type: 'workableEntityConfig', id: 'LIST' },
          { type: 'workableEntityConfig', id: arg.id },
        ]
      },
    }),
    deleteWorkableEntityConfig: builder.mutation<boolean, DeleteWorkableEntityConfigRequest>({
      query: (request) => {
        return {
          url: '/WorkableConfiguration/DeleteWorkableEntityConfig',
          method: 'POST',
          cache: 'no-cache',
          body: request,
        }
      },
      invalidatesTags: (_result, _error, arg) => {
        return [
          { type: 'workableEntityConfig', id: 'LIST' },
          { type: 'workableEntityConfig', id: arg.id },
        ]
      },
    }),
    getFxRateHistory: builder.query<FxRateHistory, GetFxRateHistoryRequest>({
      query: (request) => {
        return {
          url: '/PortalConfiguration/GetFxRateHistory',
          method: 'GET',
          cache: 'no-cache',
          params: request,
        }
      },
      providesTags: (_result, _error, arg) => {
        return [{ type: 'fxRateHistory', id: `${arg.year}-${arg.type}` }, {
          type: 'fxRateHistory',
          id: `${arg.year}-${arg.month}-${arg.type}`,
        }]
      },
    }),
    getFxRateHistories: builder.query<FxRateHistory[], GetFxRateHistoriesRequest>({
      query: (request) => {
        return {
          url: '/PortalConfiguration/GetFxRateHistories',
          method: 'GET',
          cache: 'no-cache',
          params: request,
        }
      },
      transformResponse: (response: FxRateHistory[], _meta, arg) => {
        const arr = Array.from(Array(12).keys()).map(index => {
          return {
            rates: {},
            month: index + 1,
            year: arg.year,
            type: arg.type,
            updatedAt: '',
            updatedBy: '',
          } as FxRateHistory
        })
        response.map(x => {
          arr[x.month - 1] = x
        })
        return arr
      },
      providesTags: (_result, _error, arg) => {
        return [{ type: 'fxRateHistory', id: 'LIST' }, { type: 'fxRateHistory', id: `${arg.year}-${arg.type}` }]
      },
    }),
    updateFxRateHistory: builder.mutation<boolean, UpdateFxRateHistoryRequest>({
      query: (request) => {
        return {
          url: '/PortalConfiguration/UpdateFxRateHistory',
          method: 'POST',
          cache: 'no-cache',
          body: request,
        }
      },
      invalidatesTags: [{ type: 'fxRateHistory', id: 'LIST' }],
    }),
    importFxRateHistories: builder.mutation<boolean, ImportFxRateHistoriesRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('excelFile', request.excelFile)

        return {
          url: '/PortalConfiguration/ImportFxRateHistories',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
      invalidatesTags: [{ type: 'fxRateHistory', id: 'LIST' }],
    }),
    getAllEmployeeDataAccessClaims: builder.query<GetAllEmployeeDataAccessClaimsResponse, void>({
      query: () => {
        return {
          url: '/PortalConfiguration/GetAllEmployeeDataAccessClaims',
          method: 'GET',
          cache: 'no-cache',
        }
      },
      providesTags: [{ type: 'employeeDataAccessClaim', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetWorkableEntityConfigQuery,
  useGetWorkableEntityConfigsQuery,
  useCreateWorkableEntityConfigMutation,
  useUpdateWorkableEntityConfigMutation,
  useDeleteWorkableEntityConfigMutation,
  useGetFxRateHistoryQuery,
  useGetFxRateHistoriesQuery,
  useUpdateFxRateHistoryMutation,
  useImportFxRateHistoriesMutation,
  useGetAllEmployeeDataAccessClaimsQuery,
} = portalConfigurationApi